import type { ReactNode } from 'react';
import { BASE } from './http';
import {
  dropActiveProfile,
  dropLocalSession,
  loadSession,
  saveSession,
  type Session,
} from './session';

/**
 * Sign-in and sign-out.
 *
 * The storage side lives in `session.ts`, which `http.ts` also reads; this
 * module is only the two calls that cross the network. The session primitives
 * are re-exported here so the pages that have always imported them from
 * `@/lib/auth` still can.
 */

export {
  loadSession,
  saveSession,
  loadActiveProfile,
  saveActiveProfile,
  dropLocalSession,
} from './session';
export type { AuthUser, Profile, Session } from './session';

/**
 * Sign out. Revokes the session SERVER-side first, then drops the local copy.
 *
 * Clearing sessionStorage alone left the record alive for the API's session
 * TTL, so a bearer captured before sign-out kept working — on a shared machine
 * that is the whole of the protection. The local copy is cleared even if the
 * revoke call fails, so the user is never stuck signed in.
 */
export async function clearSession(): Promise<void> {
  const token = loadSession()?.token;
  dropLocalSession();
  if (!token) return;
  try {
    await fetch(`${BASE}/v1/auth/session`, {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` },
      keepalive: true,
    });
  } catch {
    // Best-effort: the local session is already gone and the record expires.
  }
}

export class LoginError extends Error {}

export async function login(email: string, password: string): Promise<Session> {
  const res = await fetch(`${BASE}/v1/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error?: { message?: string } }).error?.message ?? 'Sign-in failed')
        : 'Sign-in failed';
    throw new LoginError(message);
  }

  const body = (await res.json()) as Session;
  if (!body.token) throw new LoginError('Sign-in failed');
  const session: Session = {
    token: body.token,
    user: body.user ?? null,
    ...(body.brands ? { brands: body.brands } : {}),
    ...(body.profiles ? { profiles: body.profiles } : {}),
  };
  saveSession(session);
  // A fresh sign-in must not inherit the last user's brand.
  dropActiveProfile();
  return session;
}

export type { ReactNode };
